import { Search, CheckCircle2, X } from 'lucide-react';
import { useState } from 'react';

export default function Hero() {
  const [domainInput, setDomainInput] = useState('');
  const [showModal, setShowModal] = useState(false);

  const handleSearch = () => {
    if (domainInput.trim()) {
      setShowModal(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-section-hero relative overflow-hidden">
      {/* Animated SVG Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Blob 1 */}
        <svg className="absolute -top-40 -right-40 w-96 h-96 opacity-20 animate-float-slow" viewBox="0 0 400 400">
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="150" fill="url(#gradient1)" filter="blur(50px)" />
        </svg>

        {/* Gradient Blob 2 */}
        <svg className="absolute top-20 -left-20 w-80 h-80 opacity-20 animate-float-medium" viewBox="0 0 400 400">
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="150" fill="url(#gradient2)" filter="blur(50px)" />
        </svg>

        {/* Gradient Blob 3 */}
        <svg className="absolute bottom-0 right-1/4 w-72 h-72 opacity-25 animate-float-fast" viewBox="0 0 400 400">
          <defs>
            <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#f43f5e" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="140" fill="url(#gradient3)" filter="blur(55px)" />
        </svg>

        {/* Animated Grid Pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-45" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="5" height="6" patternUnits="userSpaceOnUse">
              <path d="M 6 0 L 0 0 0 6" fill="none" stroke="#6b7280" strokeWidth="0.17" />
            </pattern>
            <linearGradient id="gridFade" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="white" stopOpacity="0.35" />
              <stop offset="30%" stopColor="white" stopOpacity="0.25" />
              <stop offset="60%" stopColor="white" stopOpacity="0.15" />
              <stop offset="100%" stopColor="white" stopOpacity="0.05" />
            </linearGradient>
            <mask id="gridMask">
              <rect x="0" y="0" width="100" height="100" fill="url(#gridFade)" />
            </mask>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" mask="url(#gridMask)" />
        </svg>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-purple-500 rounded-full opacity-40 animate-pulse" />
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue-500 rounded-full opacity-30 animate-pulse delay-100" />
          <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-pink-500 rounded-full opacity-40 animate-pulse delay-200" />
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-cyan-500 rounded-full opacity-30 animate-pulse delay-300" />
          <div className="absolute bottom-1/3 right-1/2 w-3 h-3 bg-indigo-500 rounded-full opacity-35 animate-pulse delay-150" />
        </div>

        {/* Animated Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M 0 50 Q 25 45 50 50 T 100 50" stroke="url(#lineGradient)" strokeWidth="0.15" fill="none" className="animate-dash" />
          <path d="M 0 35 Q 25 40 50 35 T 100 35" stroke="url(#lineGradient)" strokeWidth="0.1" fill="none" className="animate-dash delay-100" />
          <path d="M 0 65 Q 25 60 50 65 T 100 65" stroke="url(#lineGradient)" strokeWidth="0.1" fill="none" className="animate-dash delay-200" />
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
      </div >

      <div className="max-w-7xl mx-auto text-center relative z-10">
        {/* Main Tagline */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-theme-primary mb-6 leading-tight">
          Domains with credibility of both worlds
          <span className="gradient-text block mt-2">
            '.com' & '.org'
          </span>
        </h1>
        
        <p className="text-xl text-theme-secondary mb-8 max-w-3xl mx-auto">
          Premium domains with the popularity of .com and trust of .org, 
          combined with innovative branding. Secure your perfect identity today.
        </p>

        {/* Domain Search */}
        <div className="max-w-2xl mx-auto mb-8 glow">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Enter your domain name"
                className="glass-input w-full px-6 py-4 pr-28 text-lg rounded-lg"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">.com0.org</span>
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            <button 
              className="glass-button text-white px-8 py-4 rounded-lg text-lg font-semibold"
              onClick={handleSearch}
            >
              Search Domain
            </button>
          </div>
        </div>

        {/* Trust Badge */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-theme-muted">
          <div className="flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Free WHOIS Privacy
          </div>
          <div className="flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            At-Cost Pricing
          </div>
          <div className="flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            24/7 Support
          </div>
        </div>
      </div>

      {/* Domain Availability Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card rounded-2xl p-8 max-w-md w-full relative animate-fade-in">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-theme-primary mb-2">
                Domain may be Available!
              </h3>
              
              <p className="text-lg text-theme-secondary mb-6">
                <span className="font-semibold text-theme-primary">{domainInput}.com0.org</span> may be available
              </p>
              
              <p className="text-sm text-theme-muted mb-6">
                Register & claim it now before someone else does!
              </p>
              
              <a
                href="https://domains0.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block glass-button text-white px-8 py-3 rounded-lg text-lg font-semibold w-full hover:opacity-90 transition-opacity"
              >
                Register Domain
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
